import {
  createContext,
  useContext,
  useMemo,
  ReactNode,
} from 'react';
import { useAuth } from '@/contexts/auth-context';

// ============================================================================
// TYPES
// ============================================================================

type PermissionMap = Record<string, string[]>;

interface PermissionContextValue {
  permissions: PermissionMap;
  isAdmin: boolean;
  isRepresentative: boolean;
  representedDepartmentIds: Set<string>;
  hasPermission: (entity: string, action: string) => boolean;
  hasAnyPermission: (entity: string) => boolean;
  refetch: () => Promise<void>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

// ============================================================================
// HELPERS
// ============================================================================

const buildPermissionMap = (permissions: Array<{ entity: string; actions: string[] }>): PermissionMap => {
  const map: PermissionMap = {};
  for (const perm of permissions) {
    map[perm.entity] = perm.actions;
  }
  return map;
};

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated, fetchMe } = useAuth();

  const data = useMemo(() => {
    if (!user || !isAuthenticated) return {};
    return buildPermissionMap(user.permissions || []);
  }, [user, isAuthenticated]);

  const isAdmin = useMemo(() => {
    if (!user) return false;
    return (
      user.isSuperuser ||
      user.departments?.some((d) => d.name === "Admin") ||
      false
    );
  }, [user]);

  const representedDepartmentIds = useMemo(() => {
    if (!user) return new Set<string>();
    const ids = new Set<string>();
    for (const dept of user.departments ?? []) {
      if (dept.representatives?.some((r) => r.id === user.id)) {
        ids.add(dept.id);
      }
    }
    return ids;
  }, [user]);

  const isRepresentative = useMemo(
    () => representedDepartmentIds.size > 0,
    [representedDepartmentIds]
  );

  const hasPermission = useMemo(
    () => (entity: string, action: string): boolean => {
      if (user?.isSuperuser) return true;
      const entityPermissions = data?.[entity];
      return entityPermissions?.includes(action) ?? false;
    },
    [user?.isSuperuser, data]
  );

  const hasAnyPermission = useMemo(
    () => (entity: string): boolean => {
      if (user?.isSuperuser) return true;
      const entityPermissions = data?.[entity];
      return entityPermissions ? entityPermissions.length > 0 : false;
    },
    [user?.isSuperuser, data]
  );

  const contextValue = useMemo(
    () => ({
      permissions: data,
      isAdmin,
      isRepresentative,
      representedDepartmentIds,
      hasPermission,
      hasAnyPermission,
      refetch: fetchMe,
    }),
    [data, isAdmin, isRepresentative, representedDepartmentIds, hasPermission, hasAnyPermission, fetchMe]
  );

  return (
    <PermissionContext value={contextValue}>
      {children}
    </PermissionContext>
  );
};

export const usePermissions = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
